<!--
  CollectorShopSearchSheet — «عروض المحلات» (preview-shop-search). A door to the
  supplier preview that needs no collection plan: a search over the club's shops
  (GET /suppliers, which answers collectors and leaders — brief 2026-10-20
  supplier-roster-collectors); a tap on a row hands the shop to the parent, which
  closes the sheet and asks the SAME confirm the shop's page asks.

  With no signal (supplier-preview-offline P2-B, L-M): the shops whose preview is
  saved on the phone come first (name + the time it was saved; his day's shops
  and the ones picked here) — a tap opens it the same way, and `enter` picks the
  copy or the live preview by itself. Each search row has ONE trailing control:
  save that shop's preview on the phone now (needs signal; it then moves up to
  the saved list). Opened with no signal, the saved list shows and one line says
  the search needs signal (the roster is not asked).

  ux-simplifier: one overlay, no title, no submit button (the row IS the action),
  no favourites / recents / settings; the field takes the focus on open; the
  confirm is not stacked on the sheet (the parent closes the sheet first, depth
  stays 1). A saved shop leaves the search list (one row per shop, no download
  control to explain on a shop that is already saved). Staff copy, written
  directly (collector.preview.*).
-->
<template>
  <f7-sheet
    class="sp-search-sheet"
    :opened="opened"
    backdrop
    close-by-backdrop-click
    @sheet:open="onOpen"
    @sheet:opened="onOpened"
    @sheet:closed="onClosed"
  >
    <f7-page-content>
      <div ref="body" class="sp-search-body">
        <div class="sp-search-bar">
          <input
            v-model="query"
            class="sp-search-input"
            type="search"
            autocomplete="off"
            :placeholder="t('collector.preview.searchPlaceholder')"
          />
          <f7-link
            sheet-close
            icon-f7="xmark"
            icon-size="20"
            class="sp-search-close"
            :aria-label="t('general.close')"
          ></f7-link>
        </div>
        <template v-if="savedMatches.length">
          <p class="sp-search-heading">
            {{ t('collector.preview.savedHeading') }}
          </p>
          <ul class="sp-search-list sp-saved-list">
            <li v-for="shop in savedMatches" :key="shop.id">
              <button type="button" class="sp-search-row sp-saved-row" @click="pick(shop)">
                <span class="sp-search-name">{{ shop.name }}</span>
                <span class="sp-saved-time">{{ shop.time }}</span>
              </button>
            </li>
          </ul>
        </template>
        <p v-if="!online" class="sp-search-note">
          {{ t('collector.preview.searchNeedsSignal') }}
        </p>
        <p v-else-if="loading" class="sp-search-note">
          {{ t('collector.preview.searchLoading') }}
        </p>
        <div v-else-if="failed" class="sp-search-note">
          <button type="button" class="sp-search-retry" @click="load()">
            {{ t('collector.preview.searchRetry') }}
          </button>
        </div>
        <p v-else-if="!matches.length" class="sp-search-note">
          {{ t('collector.preview.searchEmpty') }}
        </p>
        <ul v-else class="sp-search-list">
          <li v-for="shop in matches" :key="shop.id" class="sp-search-item">
            <button type="button" class="sp-search-row" @click="pick(shop)">
              {{ shop.name }}
            </button>
            <button
              type="button"
              class="sp-search-save"
              :disabled="savingId != null"
              :aria-label="t('collector.preview.saveLabel')"
              @click="save(shop)"
            >
              <f7-preloader v-if="savingId === String(shop.id)" :size="20"></f7-preloader>
              <f7-icon v-else f7="arrow_down_circle" size="24"></f7-icon>
            </button>
          </li>
        </ul>
      </div>
    </f7-page-content>
  </f7-sheet>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatClock } from '@/collector/dayView.js';
import { collectorNetwork } from '@/collector/network.js';
import { useSupplierList } from '@/composables/commerce/useSupplierList.js';
import { useToast } from '@/composables/useToast.js';
import { previewReadyAt, useCollectorPrewarmStore } from '@/stores/collectorPrewarmStore.js';

defineOptions({
  name: 'CollectorShopSearchSheet',
});

const props = defineProps({
  opened: { type: Boolean, default: false },
});

const emit = defineEmits(['update:opened', 'pick']);

const { t } = useI18n();
const { suppliers, loading, failed, load } = useSupplierList();
const { showError } = useToast();

const prewarm = useCollectorPrewarmStore();
const network = collectorNetwork();
const online = computed(() => network.isOnline.value !== false);

const query = ref('');
const body = ref(null);
const savingId = ref(null);

const byName = (needle) => (shop) => !needle || String(shop.name ?? '').toLowerCase().includes(needle);
const needle = computed(() => query.value.trim().toLowerCase());

/** The previews on the phone that may be opened, newest first. */
const saved = computed(() =>
  Object.values(prewarm.copies)
    .map((row) => ({ id: row.shopId, name: row.name, readyAt: previewReadyAt(prewarm.copies, row.shopId) }))
    .filter((row) => row.readyAt != null)
    .sort((a, b) => Date.parse(b.readyAt) - Date.parse(a.readyAt))
    .map((row) => ({ id: row.id, name: row.name, time: formatClock(row.readyAt) })),
);
const savedMatches = computed(() => saved.value.filter(byName(needle.value)));

const matches = computed(() => {
  const savedIds = new Set(saved.value.map((shop) => String(shop.id)));
  return suppliers.value.filter((shop) => !savedIds.has(String(shop.id))).filter(byName(needle.value));
});

// A failed read: the no-signal toast once per failure; the retry link stays.
watch(failed, (value) => {
  if (value) showError(t('collector.preview.noSignal'));
});

function onOpen() {
  query.value = '';
  // With no signal the roster is not asked: the saved list and one line instead.
  if (online.value) load();
}

/** Save the shop's preview on the phone now; it then moves up to the saved list. */
async function save(shop) {
  if (savingId.value != null) return;
  if (!online.value) {
    showError(t('collector.preview.notSaved'));
    return;
  }
  savingId.value = String(shop.id);
  try {
    await prewarm.savePicked(shop.id, shop.name);
  } catch (error) {
    console.warn('[CollectorShopSearchSheet] the preview was not saved', error?.message ?? error);
    showError(error?.isAxiosError && error.response == null ? t('collector.preview.noSignal') : t('common.errorTryAgain'));
  } finally {
    savingId.value = null;
  }
}

function onOpened() {
  body.value?.querySelector('input')?.focus();
}

function onClosed() {
  if (props.opened) emit('update:opened', false);
}

function pick(shop) {
  emit('pick', { id: shop.id, name: shop.name });
  emit('update:opened', false);
}
</script>

<style scoped>
.sp-search-sheet {
  height: 75%;
}

.sp-search-body {
  display: flex;
  flex-direction: column;
  padding: 8px 12px 16px;
}

.sp-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sp-search-input {
  flex: 1;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid var(--f7-list-border-color, rgba(0, 0, 0, 0.2));
  border-radius: 10px;
  font-size: 16px;
  background: transparent;
  color: inherit;
}

.sp-search-close {
  min-width: 44px;
  min-height: 44px;
  justify-content: center;
}

.sp-search-note {
  margin: 24px 0;
  text-align: center;
  opacity: 0.7;
}

.sp-search-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}

.sp-search-row {
  appearance: none;
  display: block;
  width: 100%;
  min-height: 48px;
  padding: 0 4px;
  border: 0;
  border-bottom: 1px solid var(--f7-list-border-color, rgba(0, 0, 0, 0.1));
  background: none;
  color: inherit;
  font-size: 16px;
  text-align: start;
}

.sp-search-heading {
  margin: 16px 4px 0;
  font-size: 13px;
  opacity: 0.7;
}

.sp-saved-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sp-saved-time {
  flex: none;
  font-size: 13px;
  opacity: 0.7;
}

.sp-search-item {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid var(--f7-list-border-color, rgba(0, 0, 0, 0.1));
}

.sp-search-item .sp-search-row {
  flex: 1;
  min-width: 0;
  border-bottom: 0;
}

.sp-search-save {
  appearance: none;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 48px;
  border: 0;
  background: none;
  color: var(--f7-theme-color);
}

.sp-search-save:disabled {
  opacity: 0.5;
}

.sp-search-retry {
  appearance: none;
  border: 0;
  background: none;
  color: var(--f7-theme-color);
  font-size: 16px;
  text-decoration: underline;
}
</style>
